import React, { useState, useMemo, useEffect, useCallback } from "react";
import { C, getPerms, canEditModule } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  DataTable, EmptyState, useToast, filterByOwner
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  getEDawahPreachers,
  getEDawahConversations,
  getEDawahNewMuslims,
  getEDawahRetention,
  createEDawahPreacher,
  createEDawahConversation,
  createEDawahNewMuslim,
} from "./supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  Globe, MessageSquare, Mic, Heart, Bell,
  BarChart3, Star, MapPin, Archive, Award, BookOpen,
  Activity, Plus, Search, Headphones, Video, Image as ImageIcon,
  AlertCircle, TrendingUp, PieChart as PieChartIcon,
  FileText, CheckCircle, Edit, Calendar
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ E-Dawah Page — الدعوة الإلكترونية
   ═════════════════════════════════════════════ */

const CONVERSATION_STATUSES = [
  { key: "new",        label: "جديدة",         color: C.teal },
  { key: "active",     label: "جارية",         color: "#0891b2" },
  { key: "interested", label: "مهتم",          color: C.accent },
  { key: "learning",   label: "تحت التعليم",   color: "#7c3aed" },
  { key: "converted",  label: "أعلن إسلامه",   color: C.success },
  { key: "paused",     label: "متوقف",         color: C.warning },
  { key: "closed",     label: "مغلق",          color: C.textLight },
];

const PREACHER_STATUSES = [
  { key: "active",   label: "نشط",     color: C.success },
  { key: "paused",   label: "متوقف",   color: C.warning },
  { key: "inactive", label: "غير نشط", color: C.textLight },
];

const PLATFORMS = ["واتساب", "تليجرام", "إنستاجرام", "فيسبوك", "تويتر", "Zoom", "Skype", "Discord"];
const LANGUAGES = ["العربية", "الإنجليزية", "الفرنسية", "الإسبانية", "الأردية", "الهندية", "الإندونيسية", "الماليزية", "التركية", "الفلبينية", "الصينية", "الروسية"];
const COUNTRIES_LIST = ["السعودية", "الإمارات", "مصر", "الفلبين", "الهند", "باكستان", "إندونيسيا", "ماليزيا", "بريطانيا", "أمريكا", "كندا", "ألمانيا", "فرنسا", "إسبانيا", "البرازيل", "تركيا", "نيجيريا", "كينيا", "اليابان", "روسيا"];
const GENDERS = ["ذكر", "أنثى"];

const EDUCATION_LEVELS = [
  { key: "L1", label: "أساسيات النطق بالشهادتين" },
  { key: "L2", label: "أركان الإسلام" },
  { key: "L3", label: "أركان الإيمان" },
  { key: "L4", label: "أحكام أساسية" },
  { key: "L5", label: "تعمق" },
];

const FOLLOW_STATUSES = ["منتظم", "متابعة دورية", "يحتاج متابعة", "منقطع"];
const PRIORITY_COLORS = { "منخفض": C.teal, "متوسط": C.warning, "مرتفع": C.danger };

/* ─────────────────────────────────────────────
   ✦ Seed Data
   ───────────────────────────────────────────── */
/* ─── محوّلات الصفوف: snake_case (Supabase) → camelCase (الواجهة) ─── */
const mapPreacher = r => ({
  id: r.id,
  name: r.name || "",
  gender: r.gender || "",
  languages: Array.isArray(r.languages) ? r.languages : [],
  specialty: r.specialty || "",
  platforms: Array.isArray(r.platforms) ? r.platforms : [],
  country: r.country || "",
  joinDate: r.join_date || "",
  activeChats: r.active_chats ?? 0,
  newMuslims: r.new_muslims ?? 0,
  rating: Number(r.rating) || 0,
  status: r.status || "active",
});

const mapConversation = r => ({
  id: r.id,
  preacher: r.preacher || "",
  contactName: r.contact_name || "",
  country: r.country || "",
  language: r.language || "",
  platform: r.platform || "",
  startDate: r.start_date || "",
  lastActivity: r.last_activity || "",
  status: r.status || "new",
  notes: r.notes || "",
  createdBy: r.created_by || null,
});

const mapNewMuslim = r => ({
  id: r.id,
  nameBeforeIslam: r.name_before_islam || "",
  nameAfterIslam: r.name_after_islam || "",
  gender: r.gender || "",
  nationality: r.nationality || "",
  language: r.language || "",
  conversionDate: r.conversion_date || "",
  preacher: r.preacher || "",
  sourceChat: r.source_chat || "",
  documentation: r.documentation || {},
  certificate: r.certificate || {},
  currentLevel: r.current_level || "L1",
  followStatus: r.follow_status || "",
  levelProgress: r.level_progress || {},
  levelNotes: r.level_notes || {},
  createdBy: r.created_by || null,
});

const mapRetention = r => ({
  id: r.id,
  preacherId: r.preacher_id || "",
  newMuslimId: r.new_muslim_id || "",
  contactDate: r.contact_date || "",
  method: r.method || "",
  outcome: r.outcome || "",
  notes: r.notes || "",
  createdBy: r.created_by || null,
});

const NEW_MUSLIMS_BY_MONTH = [];
const PREACHER_RESPONSE_RATE = {};
const PREACHER_SUPERVISOR_NOTES = {};
/* ═════════════════════════════════════════════
   ✦ Main Page Component
   ═════════════════════════════════════════════ */
export default function EDawahPage({ user }) {
  const [tab, setTab] = useState("overview");
  const perms = useMemo(() => getPerms(user), [user]);
  const canEdit = canEditModule(perms, "edawah");

  // بيانات حيّة من Supabase — لا بيانات وهمية
  const [allPreachers, setAllPreachers]         = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [allNewMuslims, setAllNewMuslims]       = useState([]);
  const [allRetention, setAllRetention]         = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [loadError, setLoadError]               = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [pr, cv, nm, rt] = await Promise.all([
        getEDawahPreachers(),
        getEDawahConversations(),
        getEDawahNewMuslims(),
        getEDawahRetention(),
      ]);
      const rows = r => (Array.isArray(r) ? r : r?.data || []);
      setAllPreachers(rows(pr).map(mapPreacher));
      setAllConversations(rows(cv).map(mapConversation));
      setAllNewMuslims(rows(nm).map(mapNewMuslim));
      setAllRetention(rows(rt).map(mapRetention));
    } catch (e) {
      setLoadError("تعذّر تحميل بيانات الدعوة الإلكترونية");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // الداعية يرى بياناته فقط — filterByOwner يحترم نطاق الصلاحيات
  const preachers = allPreachers;
  const conversations = useMemo(
    () => filterByOwner(allConversations, user, perms),
    [allConversations, user, perms]
  );
  const newMuslims = useMemo(
    () => filterByOwner(allNewMuslims, user, perms),
    [allNewMuslims, user, perms]
  );
  const retention = useMemo(
    () => filterByOwner(allRetention, user, perms),
    [allRetention, user, perms]
  );

  const tabs = [
    { key: "overview",      label: "لوحة التحكم" },
    { key: "preachers",     label: "الدعاة الإلكترونيون" },
    { key: "conversations", label: "المحاورات" },
    { key: "newMuslims",    label: "المسلمون الجدد" },
    { key: "education",     label: "التعليم والمتابعة" },
    { key: "retention",     label: "المتابعة والاستبقاء" },
    { key: "reports",       label: "التقارير" },
    { key: "rating",        label: "تقييم الدعاة" },
    { key: "geography",     label: "الدول واللغات" },
    { key: "archive",       label: "التوثيق والأرشفة" },
  ];

  const reportSources = [
    {
      label: "المسلمون الجدد", mode: "monthly", dateField: "conversionDate", rows: newMuslims,
      columns: [
        { key: "nameAfterIslam", label: "الاسم بعد الإسلام" },
        { key: "nationality", label: "الجنسية" },
        { key: "conversionDate", label: "تاريخ الإسلام" },
        { key: "preacher", label: "الداعية" },
        { key: "currentLevel", label: "المستوى" },
        { key: "followStatus", label: "المتابعة" },
      ],
    },
    {
      label: "الحوارات", mode: "monthly", dateField: "startDate", rows: conversations,
      columns: [
        { key: "contactName", label: "المُحاوَر" },
        { key: "country", label: "الدولة" },
        { key: "language", label: "اللغة" },
        { key: "platform", label: "المنصّة" },
        { key: "startDate", label: "بدء الحوار" },
        { key: "status", label: "الحالة" },
      ],
    },
    { label: "الدعاة", mode: "cumulative", rows: preachers },
    { label: "متابعة الاحتفاظ", mode: "cumulative", rows: retention },
  ];

  return (
    <div>
      <ModuleHeader
        icon={Globe}
        title="الدعوة الإلكترونية"
        subtitle="إدارة الحوار الإلكتروني مع غير المسلمين ومتابعة الأخوة المسلمين الجدد"
      >
        <MonthlyReportButton moduleTitle="الدعوة الإلكترونية" sources={reportSources} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {loadError && (
        <Card style={{ padding: 12, marginTop: 16, background: `${C.danger}10`, border: `1px solid ${C.danger}40`, color: C.danger, fontSize: 13 }}>
          {loadError}
        </Card>
      )}

      {loading ? (
        <Card style={{ padding: 32, marginTop: 16, textAlign: "center", color: C.textLight, fontSize: 14 }}>
          جارٍ تحميل بيانات الدعوة الإلكترونية...
        </Card>
      ) : (
      <>
      {tab === "overview"      && <OverviewTab preachers={preachers} conversations={conversations} newMuslims={newMuslims} />}
      {tab === "preachers"     && <PreachersTab canEdit={canEdit} initialRows={preachers} />}
      {tab === "conversations" && <ConversationsTab canEdit={canEdit} initialRows={conversations} preachers={preachers} />}
      {tab === "newMuslims"    && <NewMuslimsTab canEdit={canEdit} initialRows={newMuslims} preachers={preachers} />}
      {tab === "education"     && <EducationTab canEdit={canEdit} initialRows={newMuslims} />}
      {tab === "retention"     && <RetentionTab rows={retention} conversations={conversations} newMuslims={newMuslims} />}
      {tab === "reports"       && <EDawahReportsTab preachers={preachers} conversations={conversations} newMuslims={newMuslims} />}
      {tab === "rating"        && <RatingTab preachers={preachers} conversations={conversations} newMuslims={newMuslims} />}
      {tab === "geography"     && <GeographyTab preachers={preachers} newMuslims={newMuslims} />}
      {tab === "archive"       && <ArchiveTab newMuslims={newMuslims} />}
      </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 1) Overview Tab — لوحة التحكم
   ───────────────────────────────────────────── */
function OverviewTab({ preachers, conversations, newMuslims }) {
  const totalLanguages = new Set(preachers.flatMap(p => p.languages)).size;
  const totalCountries = new Set([
    ...conversations.map(c => c.country),
    ...newMuslims.map(n => n.nationality),
  ]).size;
  const activeChats   = conversations.filter(c => ["new", "active", "interested", "learning"].includes(c.status)).length;
  const interested    = conversations.filter(c => c.status === "interested").length;
  const certificates  = newMuslims.filter(n => n.certificate?.number).length;
  const sessionsMonth = conversations.filter(c => (c.lastActivity || "").startsWith("2026-06")).length;

  const statusData = CONVERSATION_STATUSES.map(s => ({
    name:  s.label,
    value: conversations.filter(c => c.status === s.key).length,
    color: s.color,
  })).filter(d => d.value > 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="الدعاة الإلكترونيون" value={preachers.length}         icon={Mic}           color={C.primary} />
        <KPICard title="المحاورات النشطة"     value={activeChats}              icon={MessageSquare} color={C.teal} />
        <KPICard title="المهتمون"             value={interested}               icon={Heart}         color={C.accent} />
        <KPICard title="المسلمون الجدد"        value={newMuslims.length}         icon={Star}          color={C.success} />
        <KPICard title="الدول الممثلة"         value={totalCountries}           icon={MapPin}        color="#0891b2" />
        <KPICard title="اللغات"                value={totalLanguages}           icon={BookOpen}      color="#7c3aed" />
        <KPICard title="الشهادات الصادرة"      value={certificates}             icon={Award}         color={C.accent} />
        <KPICard title="الجلسات هذا الشهر"     value={sessionsMonth}            icon={Calendar}      color="#ea580c" />
      </div>

      <div style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع المحاورات حسب الحالة</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                {statusData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} محاورة`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {statusData.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>المسلمون الجدد شهرياً (12 شهر)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={NEW_MUSLIMS_BY_MONTH}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} مهتدٍ`]}
              />
              <Bar dataKey="value" name="المهتدون" radius={[6, 6, 0, 0]} fill={C.teal} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 2) Preachers Tab — الدعاة الإلكترونيون
   ───────────────────────────────────────────── */
const INITIAL_PREACHER = {
  name: "", gender: "ذكر", languages: "", specialty: "",
  platforms: "", country: "السعودية", joinDate: "",
  activeChats: 0, newMuslims: 0, rating: 0, status: "active",
};

function PreachersTab({ canEdit, initialRows }) {
  const [preachers, setPreachers] = useState(initialRows || []);
  useEffect(() => { setPreachers(initialRows || []); }, [initialRows]);
  const [search, setSearch] = useState("");
  const [filterLang, setFilterLang] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PREACHER);
  const [showToast, ToastView] = useToast();

  const splitList = s => s ? s.split(/،|,|\n/).map(x => x.trim()).filter(Boolean) : [];

  async function handleSave() {
    if (!form.name || !form.specialty) {
      showToast("الاسم والتخصص مطلوبان", "error");
      return;
    }
    const payload = {
      name: form.name,
      gender: form.gender,
      languages: splitList(form.languages),
      specialty: form.specialty,
      platforms: splitList(form.platforms),
      country: form.country || "السعودية",
      join_date: form.joinDate || null,
      active_chats: Number(form.activeChats) || 0,
      new_muslims: Number(form.newMuslims) || 0,
      rating: Number(form.rating) || 0,
      status: form.status || "active",
    };
    const { data, error } = await createEDawahPreacher(payload);
    if (error) { showToast(error || "تعذّر حفظ الداعية", "error"); return; }
    if (data) setPreachers(prev => [{
      id: data.id,
      name: data.name,
      gender: data.gender,
      languages: data.languages || [],
      specialty: data.specialty,
      platforms: data.platforms || [],
      country: data.country,
      joinDate: data.join_date,
      activeChats: data.active_chats,
      newMuslims: data.new_muslims,
      rating: data.rating,
      status: data.status,
    }, ...prev]);
    setForm(INITIAL_PREACHER);
    setShowAdd(false);
    showToast("تمت إضافة الداعية بنجاح");
  }

  const filtered = preachers.filter(p => {
    if (search && !p.name.includes(search) && !p.specialty.includes(search)) return false;
    if (filterLang !== "all" && !(p.languages || []).includes(filterLang)) return false;
    if (filterStatus !== "all" && p.status !== filterStatus) return false;
    return true;
  });

  const columns = [
    { key: "name",      label: "الاسم",       nowrap: true },
    { key: "gender",    label: "النوع",       nowrap: true },
    { key: "languages", label: "اللغات",      render: (v) => (v || []).join("، ") },
    { key: "specialty", label: "التخصص" },
    { key: "platforms", label: "المنصات",     render: (v) => (v || []).join("، ") },
    { key: "country",   label: "الدولة",      nowrap: true },
    { key: "activeChats", label: "محاورات نشطة", nowrap: true },
    { key: "newMuslims",  label: "مهتدون",      nowrap: true },
    { key: "rating",    label: "التقييم",     nowrap: true, render: (v) => <span style={{ fontWeight: 700, color: C.accent }}>{Number(v).toFixed(1)}</span> },
    { key: "status",    label: "الحالة",      nowrap: true, render: (v) => {
      const s = PREACHER_STATUSES.find(x => x.key === v);
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{s?.label}</span>;
    } },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={14} color={C.textLight} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو التخصص..."
            style={styles.searchInput}
          />
        </div>
        <select style={styles.filterSelect} value={filterLang} onChange={e => setFilterLang(e.target.value)}>
          <option value="all">جميع اللغات</option>
          {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع الحالات</option>
          {PREACHER_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة داعية</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="إضافة داعية جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_PREACHER); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم" required value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={GENDERS} value={form.gender} onChange={v => setForm({ ...form, gender: v })} />
              <Field label="الدولة" type="select" options={COUNTRIES_LIST} value={form.country} onChange={v => setForm({ ...form, country: v })} />
            </div>
            <Field label="اللغات (مفصولة بفواصل)" placeholder="العربية، الإنجليزية..." value={form.languages} onChange={v => setForm({ ...form, languages: v })} />
            <Field label="التخصص" required placeholder="مقارنة الأديان، الفقه..." value={form.specialty} onChange={v => setForm({ ...form, specialty: v })} />
            <Field label="المنصات (مفصولة بفواصل)" placeholder="واتساب، تليجرام..." value={form.platforms} onChange={v => setForm({ ...form, platforms: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ الانضمام" type="date" value={form.joinDate} onChange={v => setForm({ ...form, joinDate: v })} />
              <Field label="محاورات نشطة" type="number" value={form.activeChats} onChange={v => setForm({ ...form, activeChats: v })} />
              <Field label="عدد المهتدين" type="number" value={form.newMuslims} onChange={v => setForm({ ...form, newMuslims: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="التقييم (0-5)" type="number" min={0} max={5} step={0.1} value={form.rating} onChange={v => setForm({ ...form, rating: v })} />
              <Field label="الحالة" type="select" options={PREACHER_STATUSES.map(s => ({ value: s.key, label: s.label }))} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PREACHER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 3) Conversations Tab — المحاورات
   ───────────────────────────────────────────── */
const INITIAL_CONVERSATION = {
  preacher: "", contactName: "", country: "السعودية",
  language: "العربية", platform: "واتساب",
  startDate: "", lastActivity: "", status: "new", notes: "",
};

function ConversationsTab({ canEdit, initialRows, preachers = [] }) {
  const [rows, setRows] = useState(initialRows);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_CONVERSATION);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.preacher || !form.contactName) {
      showToast("الداعية واسم المحاور مطلوبان", "error");
      return;
    }
    const payload = {
      preacher: form.preacher,
      contact_name: form.contactName,
      country: form.country,
      language: form.language,
      platform: form.platform,
      start_date: form.startDate || null,
      last_activity: form.lastActivity || null,
      status: form.status,
      notes: form.notes || "",
    };
    const { data, error } = await createEDawahConversation(payload);
    if (error) { showToast(error || "تعذّر حفظ المحاورة", "error"); return; }
    if (data) setRows(prev => [{
      id: data.id,
      preacher: data.preacher,
      contactName: data.contact_name,
      country: data.country,
      language: data.language,
      platform: data.platform,
      startDate: data.start_date,
      lastActivity: data.last_activity,
      status: data.status,
      notes: data.notes,
    }, ...prev]);
    setForm(INITIAL_CONVERSATION);
    setShowAdd(false);
    showToast("تمت إضافة المحاورة بنجاح");
  }

  const filtered = rows.filter(r => {
    if (search && !r.contactName.includes(search) && !r.preacher.includes(search)) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    return true;
  });

  const columns = [
    { key: "contactName",  label: "المحاور",     nowrap: true },
    { key: "preacher",     label: "الداعية",     nowrap: true },
    { key: "country",      label: "الدولة",      nowrap: true },
    { key: "language",     label: "اللغة",       nowrap: true },
    { key: "platform",     label: "المنصة",      nowrap: true },
    { key: "startDate",    label: "بداية الحوار", nowrap: true },
    { key: "lastActivity", label: "آخر نشاط",     nowrap: true },
    { key: "status",       label: "الحالة",       nowrap: true, render: (v) => {
      const s = CONVERSATION_STATUSES.find(x => x.key === v);
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{s?.label}</span>;
    } },
    { key: "notes", label: "ملاحظات" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={14} color={C.textLight} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو الداعية..."
            style={styles.searchInput}
          />
        </div>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع الحالات</option>
          {CONVERSATION_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>محاورة جديدة</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="محاورة جديدة" onClose={() => { setShowAdd(false); setForm(INITIAL_CONVERSATION); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الداعية" required type="select" options={preachers.map(p => p.name)} value={form.preacher} onChange={v => setForm({ ...form, preacher: v })} />
              <Field label="اسم المحاور" required value={form.contactName} onChange={v => setForm({ ...form, contactName: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الدولة" type="select" options={COUNTRIES_LIST} value={form.country} onChange={v => setForm({ ...form, country: v })} />
              <Field label="اللغة" type="select" options={LANGUAGES} value={form.language} onChange={v => setForm({ ...form, language: v })} />
              <Field label="المنصة" type="select" options={PLATFORMS} value={form.platform} onChange={v => setForm({ ...form, platform: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ البداية" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
              <Field label="آخر نشاط" type="date" value={form.lastActivity} onChange={v => setForm({ ...form, lastActivity: v })} />
              <Field label="الحالة" type="select" options={CONVERSATION_STATUSES.map(s => ({ value: s.key, label: s.label }))} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={3} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_CONVERSATION); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 4) New Muslims Tab — المسلمون الجدد
   ───────────────────────────────────────────── */
const INITIAL_NEW_MUSLIM = {
  nameBeforeIslam: "", nameAfterIslam: "", gender: "ذكر",
  nationality: "أمريكا", language: "الإنجليزية",
  conversionDate: "", preacher: "", sourceChat: "",
  docAudio: false, docVideo: false, docPhoto: false,
  certNumber: "", certIssueDate: "", certPdfRef: "", certDelivered: false,
  currentLevel: "L1", followStatus: "متابعة دورية",
};

function NewMuslimsTab({ canEdit, initialRows, preachers = [] }) {
  const [rows, setRows] = useState(initialRows);
  const [search, setSearch] = useState("");
  const [filterFollow, setFilterFollow] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_NEW_MUSLIM);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.nameBeforeIslam || !form.nameAfterIslam || !form.preacher) {
      showToast("الاسم قبل/بعد والداعية مطلوبة", "error");
      return;
    }
    const payload = {
      name_before_islam: form.nameBeforeIslam,
      name_after_islam: form.nameAfterIslam,
      gender: form.gender,
      nationality: form.nationality,
      language: form.language,
      conversion_date: form.conversionDate || null,
      preacher: form.preacher,
      source_chat: form.sourceChat || null,
      documentation: { audio: form.docAudio, video: form.docVideo, photo: form.docPhoto },
      certificate: {
        number: form.certNumber || "",
        issueDate: form.certIssueDate || "",
        pdfRef: form.certPdfRef || "",
        delivered: form.certDelivered,
      },
      current_level: form.currentLevel,
      follow_status: form.followStatus,
      level_progress: { L1: 0, L2: 0, L3: 0, L4: 0, L5: 0 },
      level_notes: {},
    };
    const { data, error } = await createEDawahNewMuslim(payload);
    if (error) { showToast(error || "تعذّر تسجيل المسلم الجديد", "error"); return; }
    if (data) setRows(prev => [{
      id: data.id,
      nameBeforeIslam: data.name_before_islam,
      nameAfterIslam: data.name_after_islam,
      gender: data.gender,
      nationality: data.nationality,
      language: data.language,
      conversionDate: data.conversion_date,
      preacher: data.preacher,
      sourceChat: data.source_chat,
      documentation: data.documentation,
      certificate: data.certificate,
      currentLevel: data.current_level,
      followStatus: data.follow_status,
      levelProgress: data.level_progress,
      levelNotes: data.level_notes,
    }, ...prev]);
    setForm(INITIAL_NEW_MUSLIM);
    setShowAdd(false);
    showToast("تم تسجيل المسلم الجديد بنجاح");
  }

  const filtered = rows.filter(r => {
    if (search && !r.nameAfterIslam.includes(search) && !r.nameBeforeIslam.includes(search) && !r.preacher.includes(search)) return false;
    if (filterFollow !== "all" && r.followStatus !== filterFollow) return false;
    return true;
  });

  const columns = [
    { key: "nameAfterIslam",   label: "الاسم بعد الإسلام", nowrap: true },
    { key: "nameBeforeIslam",  label: "الاسم قبل الإسلام", nowrap: true },
    { key: "gender",           label: "النوع",             nowrap: true },
    { key: "nationality",      label: "الجنسية",           nowrap: true },
    { key: "language",         label: "اللغة",             nowrap: true },
    { key: "conversionDate",   label: "تاريخ الإسلام",     nowrap: true },
    { key: "preacher",         label: "الداعية",           nowrap: true },
    { key: "currentLevel",     label: "المستوى",           nowrap: true, render: (v) => {
      const lv = EDUCATION_LEVELS.find(l => l.key === v);
      return <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${C.teal}15`, color: C.teal }}>{lv?.label || v}</span>;
    } },
    { key: "followStatus",     label: "حالة المتابعة",     nowrap: true },
    { key: "certificate",      label: "الشهادة",           nowrap: true, render: (v) => v?.delivered
      ? <span style={{ color: C.success, fontWeight: 600 }}>{v.number}</span>
      : <span style={{ color: C.warning, fontWeight: 600 }}>{v?.number ? "بانتظار التسليم" : "—"}</span>
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={14} color={C.textLight} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو الداعية..."
            style={styles.searchInput}
          />
        </div>
        <select style={styles.filterSelect} value={filterFollow} onChange={e => setFilterFollow(e.target.value)}>
          <option value="all">جميع حالات المتابعة</option>
          {FOLLOW_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)}>تسجيل مسلم جديد</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="تسجيل مسلم جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_NEW_MUSLIM); }} width={760}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الاسم قبل الإسلام" required value={form.nameBeforeIslam} onChange={v => setForm({ ...form, nameBeforeIslam: v })} />
              <Field label="الاسم بعد الإسلام" required value={form.nameAfterIslam} onChange={v => setForm({ ...form, nameAfterIslam: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={GENDERS} value={form.gender} onChange={v => setForm({ ...form, gender: v })} />
              <Field label="الجنسية" type="select" options={COUNTRIES_LIST} value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="اللغة" type="select" options={LANGUAGES} value={form.language} onChange={v => setForm({ ...form, language: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ الإسلام" type="date" value={form.conversionDate} onChange={v => setForm({ ...form, conversionDate: v })} />
              <Field label="الداعية" required type="select" options={preachers.map(p => p.name)} value={form.preacher} onChange={v => setForm({ ...form, preacher: v })} />
              <Field label="المحاورة المصدر (ID)" placeholder="c1, c2..." value={form.sourceChat} onChange={v => setForm({ ...form, sourceChat: v })} />
            </div>

            <div style={styles.sectionDivider}>التوثيق</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <CheckboxField label="تسجيل صوتي" checked={form.docAudio} onChange={v => setForm({ ...form, docAudio: v })} />
              <CheckboxField label="تسجيل مرئي" checked={form.docVideo} onChange={v => setForm({ ...form, docVideo: v })} />
              <CheckboxField label="صورة"       checked={form.docPhoto} onChange={v => setForm({ ...form, docPhoto: v })} />
            </div>

            <div style={styles.sectionDivider}>الشهادة</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="رقم الشهادة" value={form.certNumber} onChange={v => setForm({ ...form, certNumber: v })} />
              <Field label="تاريخ الإصدار" type="date" value={form.certIssueDate} onChange={v => setForm({ ...form, certIssueDate: v })} />
              <Field label="مرجع الملف PDF" placeholder="cert_xxx.pdf" value={form.certPdfRef} onChange={v => setForm({ ...form, certPdfRef: v })} />
            </div>
            <CheckboxField label="تم تسليم الشهادة" checked={form.certDelivered} onChange={v => setForm({ ...form, certDelivered: v })} />

            <div style={styles.sectionDivider}>المتابعة</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المستوى الحالي" type="select" options={EDUCATION_LEVELS.map(l => ({ value: l.key, label: l.label }))} value={form.currentLevel} onChange={v => setForm({ ...form, currentLevel: v })} />
              <Field label="حالة المتابعة" type="select" options={FOLLOW_STATUSES} value={form.followStatus} onChange={v => setForm({ ...form, followStatus: v })} />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_NEW_MUSLIM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.primary, cursor: "pointer", fontWeight: 500 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.teal }} />
      {label}
    </label>
  );
}

/* ─────────────────────────────────────────────
   ✦ 5) Education Tab — التعليم والمتابعة
   ───────────────────────────────────────────── */
function EducationTab({ canEdit, initialRows }) {
  const [rows, setRows] = useState(initialRows);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ levelProgress: {}, levelNotes: {} });
  const [showToast, ToastView] = useToast();

  function openEdit(row) {
    setSelected(row);
    setForm({
      levelProgress: { ...row.levelProgress },
      levelNotes: { ...row.levelNotes },
    });
  }

  function handleUpdate() {
    setRows(prev => prev.map(r => r.id === selected.id ? {
      ...r,
      levelProgress: { ...form.levelProgress },
      levelNotes: { ...form.levelNotes },
    } : r));
    setSelected(null);
    showToast("تم تحديث التقدم");
  }

  const columns = [
    { key: "nameAfterIslam",   label: "الاسم",     nowrap: true },
    { key: "preacher",         label: "الداعية",   nowrap: true },
    ...EDUCATION_LEVELS.map(lv => ({
      key: `progress_${lv.key}`,
      label: lv.label,
      render: (_, row) => {
        const p = row.levelProgress?.[lv.key] || 0;
        const color = p >= 100 ? C.success : p >= 50 ? C.teal : p > 0 ? C.warning : C.textLight;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90 }}>
            <div style={{ flex: 1, height: 6, borderRadius: 3, background: C.border }}>
              <div style={{ width: `${p}%`, height: "100%", background: color, borderRadius: 3 }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 30 }}>{p}%</span>
          </div>
        );
      },
    })),
    ...(canEdit ? [{
      key: "action", label: "إجراء", nowrap: true,
      render: (_, row) => (
        <button onClick={() => openEdit(row)} style={styles.editBtn}>
          <Edit size={14} /> تحديث
        </button>
      ),
    }] : []),
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={{ marginBottom: 16, padding: 14, background: `${C.teal}08`, borderRadius: 10, fontSize: 13, color: C.primary, lineHeight: 1.8 }}>
        تتضمن المتابعة 5 مستويات: {EDUCATION_LEVELS.map(l => l.label).join(" — ")}. لكل طالب نسبة تقدم وملاحظات لكل مستوى.
      </div>

      <DataTable columns={columns} data={rows} />

      {selected && (
        <Modal title={`تحديث تقدم ${selected.nameAfterIslam}`} onClose={() => setSelected(null)} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {EDUCATION_LEVELS.map(lv => (
              <div key={lv.key} style={{ padding: 14, background: C.bg, borderRadius: 10 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary, marginBottom: 8 }}>{lv.label}</p>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 12 }}>
                  <Field
                    label="نسبة %"
                    type="number"
                    min={0}
                    max={100}
                    value={form.levelProgress[lv.key] || 0}
                    onChange={v => setForm(p => ({ ...p, levelProgress: { ...p.levelProgress, [lv.key]: Number(v) } }))}
                  />
                  <Field
                    label="ملاحظات"
                    value={form.levelNotes[lv.key] || ""}
                    onChange={v => setForm(p => ({ ...p, levelNotes: { ...p.levelNotes, [lv.key]: v } }))}
                  />
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <Btn icon={CheckCircle} onClick={handleUpdate}>حفظ التحديث</Btn>
              <Btn variant="ghost" onClick={() => setSelected(null)}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 6) Retention Tab — المتابعة والاستبقاء
   ───────────────────────────────────────────── */
function RetentionTab({ rows, conversations = [], newMuslims = [] }) {
  const alerts = rows.filter(r => r.daysSilent > 7);
  const totalContacts = conversations.length + newMuslims.length;
  const droppedOut    = newMuslims.filter(n => n.followStatus === "منقطع").length;
  const retentionRate = totalContacts > 0
    ? Math.round((1 - droppedOut / totalContacts) * 100)
    : 0;

  const columns = [
    { key: "contactName", label: "المحاور",     nowrap: true },
    { key: "preacher",    label: "الداعية",     nowrap: true },
    { key: "lastContact", label: "آخر تواصل",   nowrap: true },
    { key: "daysSilent",  label: "أيام الانقطاع", nowrap: true, render: (v) => (
      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: v > 30 ? `${C.danger}18` : v > 14 ? `${C.warning}18` : `${C.teal}18`, color: v > 30 ? C.danger : v > 14 ? C.warning : C.teal }}>
        {v} يوم
      </span>
    ) },
    { key: "priority",    label: "الأولوية",     nowrap: true, render: (v) => (
      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${PRIORITY_COLORS[v]}18`, color: PRIORITY_COLORS[v] }}>{v}</span>
    ) },
    { key: "reason",      label: "السبب" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="معدل الاستبقاء" value={`${retentionRate}%`} icon={TrendingUp}  color={C.success} />
        <KPICard title="تنبيهات انقطاع" value={alerts.length}        icon={Bell}         color={C.warning} />
        <KPICard title="حالات حرجة"     value={alerts.filter(a => a.daysSilent > 30).length} icon={AlertCircle} color={C.danger} />
        <KPICard title="إجمالي المتابعة" value={rows.length}          icon={Activity}     color={C.teal} />
      </div>

      <div style={{ padding: 14, background: `${C.warning}10`, borderRadius: 10, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <AlertCircle size={18} color={C.warning} />
        <span style={{ fontSize: 13, color: C.primary, fontWeight: 600 }}>
          الحالات التي تحتاج إلى تواصل (انقطاع أكثر من 7 أيام): {alerts.length}
        </span>
      </div>

      <DataTable columns={columns} data={[...rows].sort((a, b) => b.daysSilent - a.daysSilent)} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 7) Reports Tab — التقارير
   ───────────────────────────────────────────── */
function EDawahReportsTab({ preachers, conversations, newMuslims }) {
  const [showToast, ToastView] = useToast();
  const todayChats = conversations.filter(c => c.lastActivity === "2026-06-19").length;
  const monthConverts = newMuslims.filter(n => (n.conversionDate || "").startsWith("2026-06")).length;
  const yearConverts  = newMuslims.filter(n => (n.conversionDate || "").startsWith("2026")).length;

  function genReport(type) {
    showToast(`تم توليد التقرير ${type}`);
  }

  const reportCards = [
    { key: "daily",   label: "التقرير اليومي", icon: Calendar, color: C.teal, lines: [
      ["المحاورات النشطة اليوم", todayChats],
      ["إجمالي المحاورات",        conversations.length],
      ["الدعاة النشطون",          preachers.filter(p => p.status === "active").length],
    ] },
    { key: "monthly", label: "التقرير الشهري", icon: BarChart3, color: "#7c3aed", lines: [
      ["مهتدون هذا الشهر",        monthConverts],
      ["محاورات حالياً",          conversations.filter(c => ["new","active","interested","learning"].includes(c.status)).length],
      ["شهادات صادرة",            newMuslims.filter(n => (n.certificate?.issueDate || "").startsWith("2026-06")).length],
    ] },
    { key: "yearly",  label: "التقرير السنوي", icon: TrendingUp, color: C.success, lines: [
      ["مهتدون هذه السنة",        yearConverts],
      ["دول ممثلة",                new Set([...conversations.map(c => c.country), ...newMuslims.map(n => n.nationality)]).size],
      ["لغات الدعاة",              new Set(preachers.flatMap(p => p.languages)).size],
    ] },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المحاورات" value={conversations.length} icon={MessageSquare} color={C.teal} />
        <KPICard title="إجمالي المهتدين"  value={newMuslims.length}     icon={Heart}         color={C.success} />
        <KPICard title="الدعاة"            value={preachers.length}     icon={Mic}           color={C.primary} />
        <KPICard title="مهتدون هذا الشهر"  value={monthConverts}        icon={Star}          color={C.accent} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
        {reportCards.map(rc => {
          const Icon = rc.icon;
          return (
            <Card key={rc.key}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${rc.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={22} color={rc.color} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.primary, margin: 0 }}>{rc.label}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {rc.lines.map((ln, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: C.bg, borderRadius: 8, fontSize: 13 }}>
                    <span style={{ color: C.textLight }}>{ln[0]}</span>
                    <span style={{ fontWeight: 700, color: C.primary }}>{ln[1]}</span>
                  </div>
                ))}
              </div>
              <Btn icon={FileText} onClick={() => genReport(rc.label)}>توليد التقرير</Btn>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 8) Rating Tab — تقييم الدعاة
   ───────────────────────────────────────────── */
function RatingTab({ preachers, conversations = [], newMuslims = [] }) {
  function Stars({ value }) {
    const full = Math.floor(value);
    const half = value - full >= 0.5;
    return (
      <span style={{ display: "inline-flex", gap: 1, alignItems: "center" }}>
        {[0, 1, 2, 3, 4].map(i => {
          const isFull = i < full;
          const isHalf = i === full && half;
          return (
            <Star
              key={i}
              size={14}
              fill={isFull || isHalf ? C.accent : "transparent"}
              color={C.accent}
              strokeWidth={1.5}
              style={{ opacity: isHalf ? 0.6 : 1 }}
            />
          );
        })}
        <span style={{ marginRight: 6, fontSize: 12, fontWeight: 700, color: C.primary }}>{value.toFixed(1)}</span>
      </span>
    );
  }

  const data = preachers.map(p => ({
    ...p,
    conversations: conversations.filter(c => c.preacher === p.name).length,
    converts: newMuslims.filter(n => n.preacher === p.name).length,
    responseRate: PREACHER_RESPONSE_RATE[p.name] || 85,
    note: PREACHER_SUPERVISOR_NOTES[p.name] || "—",
  }));

  const columns = [
    { key: "name",          label: "الداعية",         nowrap: true },
    { key: "conversations", label: "عدد المحاورات",   nowrap: true },
    { key: "converts",      label: "عدد المهتدين",    nowrap: true },
    { key: "responseRate",  label: "معدل الاستجابة",  nowrap: true, render: (v) => (
      <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: v >= 90 ? `${C.success}18` : v >= 80 ? `${C.teal}18` : `${C.warning}18`, color: v >= 90 ? C.success : v >= 80 ? C.teal : C.warning }}>
        {v}%
      </span>
    ) },
    { key: "rating",        label: "التقييم العام",    nowrap: true, render: (v) => <Stars value={v} /> },
    { key: "note",          label: "ملاحظات المشرف" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="متوسط التقييم"  value={(preachers.reduce((s, p) => s + p.rating, 0) / preachers.length).toFixed(2)} icon={Star}      color={C.accent} />
        <KPICard title="أعلى تقييم"     value={Math.max(...preachers.map(p => p.rating)).toFixed(1)}                       icon={Award}      color={C.success} />
        <KPICard title="إجمالي المهتدين" value={preachers.reduce((s, p) => s + p.newMuslims, 0)}                            icon={Heart}      color={C.teal} />
        <KPICard title="إجمالي الجلسات" value={preachers.reduce((s, p) => s + p.activeChats, 0)}                           icon={Activity}  color={C.primary} />
      </div>
      <DataTable columns={columns} data={data} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 9) Geography Tab — الدول واللغات
   ───────────────────────────────────────────── */
function GeographyTab({ preachers, newMuslims }) {
  const nationalityCounts = {};
  newMuslims.forEach(n => {
    nationalityCounts[n.nationality] = (nationalityCounts[n.nationality] || 0) + 1;
  });
  const topNationalities = Object.entries(nationalityCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const langCounts = {};
  preachers.forEach(p => {
    (p.languages || []).forEach(l => {
      langCounts[l] = (langCounts[l] || 0) + 1;
    });
  });
  const colors = [C.primary, C.teal, C.accent, C.success, "#7c3aed", "#0891b2", "#ea580c", "#db2777", "#16a34a", "#6366f1", "#c026d3", "#d97706"];
  const langData = Object.entries(langCounts)
    .map(([name, value], i) => ({ name, value, color: colors[i % colors.length] }))
    .sort((a, b) => b.value - a.value);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MapPin size={18} color={C.teal} />
              <span style={styles.chartTitle}>أعلى 10 جنسيات للمهتدين</span>
            </div>
          </div>
          {topNationalities.length === 0 ? <EmptyState title="لا توجد بيانات" /> : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topNationalities} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis type="number" fontSize={11} tick={{ fill: C.textLight }} />
                <YAxis dataKey="name" type="category" fontSize={11} tick={{ fill: C.textLight }} width={90} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                  formatter={(v) => [`${v} مهتدٍ`]}
                />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} fill={C.teal} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع لغات الدعاة</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={langData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" paddingAngle={3}>
                {langData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} داعية`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {langData.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 10) Archive Tab — التوثيق والأرشفة
   ───────────────────────────────────────────── */
function ArchiveTab({ newMuslims }) {
  const archive = [];
  newMuslims.forEach(n => {
    if (n.documentation?.audio) {
      archive.push({
        id: `${n.id}-audio`, muslim: n.nameAfterIslam, type: "تسجيل صوتي",
        date: n.conversionDate, ref: `audio_${n.id}.mp3`,
        description: `تسجيل صوتي لإعلان إسلام ${n.nameAfterIslam}`,
      });
    }
    if (n.documentation?.video) {
      archive.push({
        id: `${n.id}-video`, muslim: n.nameAfterIslam, type: "تسجيل مرئي",
        date: n.conversionDate, ref: `video_${n.id}.mp4`,
        description: `تسجيل مرئي لإعلان إسلام ${n.nameAfterIslam}`,
      });
    }
    if (n.documentation?.photo) {
      archive.push({
        id: `${n.id}-photo`, muslim: n.nameAfterIslam, type: "صورة",
        date: n.conversionDate, ref: `photo_${n.id}.jpg`,
        description: `صورة توثيقية لـ ${n.nameAfterIslam}`,
      });
    }
    if (n.certificate?.number) {
      archive.push({
        id: `${n.id}-cert`, muslim: n.nameAfterIslam, type: "شهادة إسلام",
        date: n.certificate.issueDate || n.conversionDate,
        ref: n.certificate.pdfRef || `${n.certificate.number}.pdf`,
        description: `شهادة إسلام رقم ${n.certificate.number}`,
      });
    }
  });

  const typeIcons = {
    "تسجيل صوتي": Headphones,
    "تسجيل مرئي": Video,
    "صورة":       ImageIcon,
    "شهادة إسلام": Award,
  };
  const typeColors = {
    "تسجيل صوتي": C.teal,
    "تسجيل مرئي": "#7c3aed",
    "صورة":       "#0891b2",
    "شهادة إسلام": C.accent,
  };

  const columns = [
    { key: "muslim", label: "المسلم الجديد", nowrap: true },
    { key: "type",   label: "نوع الملف",    nowrap: true, render: (v) => {
      const Icon = typeIcons[v];
      const color = typeColors[v] || C.textLight;
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${color}18`, color }}>
          {Icon && <Icon size={12} />}
          {v}
        </span>
      );
    } },
    { key: "date",        label: "التاريخ", nowrap: true },
    { key: "ref",         label: "مرجع الملف", nowrap: true, render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12, color: C.primary }}>{v}</span> },
    { key: "description", label: "الوصف" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الملفات" value={archive.length}                                                  icon={Archive}     color={C.primary} />
        <KPICard title="تسجيلات صوتية"   value={archive.filter(a => a.type === "تسجيل صوتي").length}            icon={Headphones}  color={C.teal} />
        <KPICard title="تسجيلات مرئية"   value={archive.filter(a => a.type === "تسجيل مرئي").length}            icon={Video}       color="#7c3aed" />
        <KPICard title="شهادات الإسلام"  value={archive.filter(a => a.type === "شهادة إسلام").length}            icon={Award}        color={C.accent} />
      </div>
      <DataTable columns={columns} data={archive} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Styles
   ───────────────────────────────────────────── */
const styles = {
  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16,
  },
  toolbar: {
    display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer",
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20,
    marginBottom: 20,
  },
  chartCard: { padding: 20 },
  chartHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16,
  },
  chartTitle: { fontSize: 15, fontWeight: 700, color: C.primary },
  legendGrid: {
    display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 8,
  },
  legendItem: { display: "flex", alignItems: "center", gap: 4 },
  sectionDivider: {
    marginTop: 8, paddingBottom: 4,
    borderBottom: `2px solid ${C.teal}30`,
    fontSize: 13, fontWeight: 700, color: C.teal,
  },
  editBtn: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "5px 10px", borderRadius: 8,
    background: `${C.teal}12`, color: C.teal,
    border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
    fontFamily: "'Readex Pro', sans-serif",
  },
};
